import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import {
  FEED_PAGE_SIZE,
  NOTIFICATIONS_ENDPOINTS,
  isNotificationsUnavailableError,
  isUnauthorizedError,
  mergeNotifications,
  normalizeFeed,
  normalizeNotification,
  normalizeUnreadCount,
  notificationLabel,
} from '../_helpers/notifications.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { englishT } from '../locales/index.js';

/** Catalog keys of the feed's error messages; `error` / `mutationError.message` carry them resolved in the active locale. */
export const FEED_MESSAGE_KEYS = Object.freeze({
  feedError: 'notifications.feed.loadError',
  loadMoreError: 'notifications.feed.loadMoreError',
  markReadError: 'notifications.feed.markReadError',
  markAllReadError: 'notifications.feed.markAllReadError',
});

/* English copy of the messages above, for tests and callers without a locale. */
export const FEED_ERROR = englishT(FEED_MESSAGE_KEYS.feedError);
export const LOAD_MORE_ERROR = englishT(FEED_MESSAGE_KEYS.loadMoreError);
export const MARK_READ_ERROR = englishT(FEED_MESSAGE_KEYS.markReadError);
export const MARK_ALL_READ_ERROR = englishT(FEED_MESSAGE_KEYS.markAllReadError);

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
/** Once the live stream is given up on, the feed head is re-read at this cadence instead. */
export const FALLBACK_REFRESH_INTERVAL_MS = 60000;

/**
 * Feed status:
 *   'idle'         — disabled (no session), nothing requested
 *   'loading'      — first page in flight
 *   'ready'        — feed (possibly empty) loaded
 *   'error'        — first page failed; `reload()` retries
 *   'unavailable'  — backend answered 404/501: ENG-97 not shipped
 *   'unauthorized' — backend answered 401/403 (these endpoints are exempt from
 *                    fetchWrapper's auto-logout, so the feed reports it instead)
 */
export const FEED_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
  UNAVAILABLE: 'unavailable',
  UNAUTHORIZED: 'unauthorized',
});

const statusForError = (error) => {
  if (isNotificationsUnavailableError(error)) return FEED_STATUS.UNAVAILABLE;
  if (isUnauthorizedError(error)) return FEED_STATUS.UNAUTHORIZED;
  return FEED_STATUS.ERROR;
};

const feedUrl = (cursor) => {
  const params = new URLSearchParams({ limit: String(FEED_PAGE_SIZE) });
  if (cursor) params.set('cursor', cursor);
  return `${NOTIFICATIONS_ENDPOINTS.feed()}?${params}`;
};

/**
 * The listener's notification feed (ENG-26): first page + cursor paging,
 * the backend unread count, read-state mutations that keep the previous UI
 * state on failure and hand back a retry, and the live event stream that adds new
 * notifications as they happen. See `_helpers/notifications.js` for the
 * contract every request here follows.
 *
 * `enabled` is "there is a session"; while false nothing is requested and
 * the status stays idle. The stream only opens once the first page has
 * loaded, so a backend without ENG-97 (unavailable) is never dialled. Events
 * are deduped by id, a reconnect re-reads the first page and unread count to
 * recover anything missed, and after the reconnect budget is spent the head
 * is re-read on a timer instead.
 */
export function useNotifications({ enabled = true } = {}) {
  const fetchWrapperRef = useRef(useFetchWrapper());
  // The active locale's `t` and tag, read through a ref so the effects and
  // callbacks below need not re-run (and re-fetch) when the listener switches
  // language. The tag reaches the live announcement's structured message
  // formatting (ENG-382).
  const { t, locale } = useLocale();
  const tRef = useRef(t);
  tRef.current = t;
  const localeRef = useRef(locale);
  localeRef.current = locale;

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState({ count: 0, capped: false });
  const [status, setStatus] = useState(enabled ? FEED_STATUS.LOADING : FEED_STATUS.IDLE);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  // A failed read-state mutation, with the exact call to make again. The UI
  // was already rolled back to what it showed before the attempt.
  const [mutationError, setMutationError] = useState(null);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  // Latest live arrival, for an aria-live region: `{ id, text }` so the same
  // text twice still announces.
  const [announcement, setAnnouncement] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Bumped when the first page has loaded so the stream effect can start.
  const [streamKey, setStreamKey] = useState(0);

  // Latest items/unread for callbacks and stream handlers without re-binding them.
  const itemsRef = useRef(items);
  const unreadRef = useRef(unread);
  itemsRef.current = items;
  unreadRef.current = unread;

  // Bumped whenever the unread count is set outright (a backend count from
  // the feed envelope, /unread-count, the stream's ready event or a mutation response;
  // a reset on reload or sign-out) rather than nudged by one. A failed
  // mutation compares it with its own snapshot: unchanged means its optimistic
  // decrement is still the only thing between the count and the truth, so it
  // is undone; changed means the count has since been restated by something
  // that already accounts for the mutation not having happened.
  const unreadAuthorityRef = useRef(0);

  const restateUnread = useCallback((next) => {
    if (!next) return;
    unreadAuthorityRef.current += 1;
    setUnread(next);
  }, []);

  /**
   * Re-read the first page and unread count without touching the status
   * (recovery after a gap). The page is merged over what is shown, so a
   * notification the backend has since deleted stays until `reload()`; only
   * the first page is re-read, so nothing older is refreshed either.
   */
  const refreshHead = useCallback(async () => {
    const api = fetchWrapperRef.current;
    const [feedResult, unreadResult] = await Promise.allSettled([
      api.get(feedUrl(null)),
      api.get(NOTIFICATIONS_ENDPOINTS.unreadCount()),
    ]);
    if (feedResult.status === 'fulfilled') {
      const feed = normalizeFeed(feedResult.value);
      setItems((previous) => mergeNotifications(previous, feed.notifications));
      restateUnread(feed.unread);
    }
    if (unreadResult.status === 'fulfilled') restateUnread(normalizeUnreadCount(unreadResult.value));
    return feedResult.status === 'fulfilled' || unreadResult.status === 'fulfilled';
  }, [restateUnread]);

  // First page + unread count
  useEffect(() => {
    if (!enabled) {
      setStatus(FEED_STATUS.IDLE);
      setItems([]);
      restateUnread({ count: 0, capped: false });
      setNextCursor(null);
      return undefined;
    }
    let cancelled = false;
    const api = fetchWrapperRef.current;

    const load = async () => {
      setStatus(FEED_STATUS.LOADING);
      setError(null);
      setLoadMoreError(null);
      const [feedResult, unreadResult] = await Promise.allSettled([
        api.get(feedUrl(null)),
        api.get(NOTIFICATIONS_ENDPOINTS.unreadCount()),
      ]);
      if (cancelled) return;

      if (feedResult.status === 'rejected') {
        const nextStatus = statusForError(feedResult.reason);
        if (nextStatus === FEED_STATUS.ERROR) {
          console.error('Failed to load notifications:', feedResult.reason);
          setError(tRef.current(FEED_MESSAGE_KEYS.feedError));
        }
        setItems([]);
        restateUnread({ count: 0, capped: false });
        setNextCursor(null);
        setStatus(nextStatus);
        return;
      }

      const feed = normalizeFeed(feedResult.value);
      setItems(feed.notifications);
      setNextCursor(feed.nextCursor);
      if (unreadResult.status === 'fulfilled') {
        restateUnread(normalizeUnreadCount(unreadResult.value));
      } else if (feed.unread) {
        restateUnread(feed.unread);
      } else {
        // No authority answered: count what is on the page rather than claim zero.
        restateUnread({ count: feed.notifications.filter((item) => !item.read).length, capped: false });
      }
      setStatus(FEED_STATUS.READY);
      setStreamKey((key) => key + 1);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey, restateUnread]);

  // Live events. Opens only after a successful first page (streamKey > 0).
  //
  // The backend's stream is server-sent events (ENG-398): every connection
  // attempt first mints a single-use ticket over an ordinary authenticated
  // POST, then opens an EventSource with it. The browser's own EventSource
  // retry cannot be used — it reuses the URL, and the ticket in it is spent —
  // so the source is closed on the first error and this effect reconnects
  // itself, with backoff, minting afresh each time, and resuming from the
  // last notification id it saw (`?after=`). See the backend's
  // Documentation/sse-stream-tickets.md for the contract.
  useEffect(() => {
    if (!enabled || streamKey === 0) return undefined;
    const state = {
      source: null,
      reconnectTimer: null,
      fallbackTimer: null,
      reconnectAttempts: 0,
      everConnected: false,
      // The last notification id the stream delivered: where the next connection resumes.
      lastEventId: null,
      mounted: true,
    };

    const stopFallback = () => {
      if (state.fallbackTimer) {
        clearInterval(state.fallbackTimer);
        state.fallbackTimer = null;
      }
    };

    const startFallback = () => {
      if (!state.mounted || state.fallbackTimer) return;
      setConnectionStatus('polling');
      state.fallbackTimer = setInterval(() => {
        if (state.mounted) refreshHead();
      }, FALLBACK_REFRESH_INTERVAL_MS);
    };

    const closeSource = () => {
      if (!state.source) return;
      state.source.onopen = null;
      state.source.onerror = null;
      state.source.close();
      state.source = null;
    };

    /** Back off and try again, or give up on the stream once the budget is spent. */
    const scheduleReconnect = () => {
      if (!state.mounted) return;
      if (state.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        startFallback();
        return;
      }
      const delay = Math.min(INITIAL_RECONNECT_DELAY_MS * Math.pow(2, state.reconnectAttempts), MAX_RECONNECT_DELAY_MS);
      state.reconnectAttempts += 1;
      setConnectionStatus('reconnecting');
      state.reconnectTimer = setTimeout(connect, delay);
    };

    const parse = (raw) => {
      try {
        const data = JSON.parse(raw);
        return data && typeof data === 'object' ? data : null;
      } catch {
        return null; // malformed frame
      }
    };

    /** `event: ready` — the backend's unread count, sent once per connection. */
    const handleReady = (event) => {
      const data = parse(event.data);
      if (data) restateUnread(normalizeUnreadCount(data));
    };

    /** `event: notification` — one inbox notification; replays after a reconnect may repeat one. */
    const handleNotification = (event) => {
      if (event.lastEventId) state.lastEventId = event.lastEventId;
      const notification = normalizeNotification(parse(event.data));
      if (!notification) return;
      const known = itemsRef.current.find((item) => item.id === notification.id);
      setItems((previous) => mergeNotifications(previous, [notification]));
      if (!known && !notification.read) {
        // First sighting of an unread notification: one more unread.
        setUnread((previous) => ({ ...previous, count: previous.count + 1 }));
      }
      if (!known) setAnnouncement({ id: notification.id, text: notificationLabel(notification, tRef.current, localeRef.current) });
    };

    const connect = async () => {
      if (!state.mounted || state.source) return;
      if (typeof EventSource === 'undefined') {
        startFallback();
        return;
      }

      let ticket;
      try {
        const minted = await fetchWrapperRef.current.post(NOTIFICATIONS_ENDPOINTS.streamTicket());
        ticket = typeof minted?.ticket === 'string' && minted.ticket ? minted.ticket : null;
        if (!ticket) throw new Error('No stream ticket in the response');
      } catch (err) {
        if (!state.mounted) return;
        if (isUnauthorizedError(err)) {
          // The session itself was refused: minting again will not change
          // that, and the feed's own requests will report it.
          setConnectionStatus('disconnected');
          return;
        }
        if (isNotificationsUnavailableError(err)) {
          // A backend without the live stream: the head is re-read on a timer.
          startFallback();
          return;
        }
        // 429 / 503 / network: worth another try after a pause.
        scheduleReconnect();
        return;
      }
      if (!state.mounted) return;

      const source = new EventSource(NOTIFICATIONS_ENDPOINTS.stream(ticket, state.lastEventId));
      state.source = source;

      source.onopen = () => {
        if (!state.mounted) return;
        setConnectionStatus('connected');
        state.reconnectAttempts = 0;
        stopFallback();
        // The stream replays what it holds after `?after=`, but read-state
        // changes made elsewhere are only in the feed.
        if (state.everConnected) refreshHead();
        state.everConnected = true;
      };

      source.addEventListener('ready', (event) => {
        if (state.mounted) handleReady(event);
      });
      source.addEventListener('notification', (event) => {
        if (state.mounted) handleNotification(event);
      });

      source.onerror = () => {
        // A refused ticket, a dropped connection and a server that is down
        // all look the same here. Close before the browser retries the spent
        // ticket on its own, and reconnect with a fresh one.
        closeSource();
        scheduleReconnect();
      };
    };

    setConnectionStatus('connecting');
    // Deferred so StrictMode's mount → cleanup → mount never mints a ticket
    // and opens a stream it immediately abandons.
    state.reconnectTimer = setTimeout(connect, 0);

    return () => {
      state.mounted = false;
      if (state.reconnectTimer) clearTimeout(state.reconnectTimer);
      stopFallback();
      closeSource();
      setConnectionStatus('disconnected');
    };
  }, [enabled, streamKey, refreshHead, restateUnread]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const feed = normalizeFeed(await fetchWrapperRef.current.get(feedUrl(nextCursor)));
      setItems((previous) => mergeNotifications(previous, feed.notifications));
      setNextCursor(feed.nextCursor);
      restateUnread(feed.unread);
    } catch (err) {
      console.error('Failed to load more notifications:', err);
      setLoadMoreError(tRef.current(FEED_MESSAGE_KEYS.loadMoreError));
    }
    setLoadingMore(false);
  }, [nextCursor, loadingMore, restateUnread]);

  const setPending = useCallback((id, pending) => {
    setPendingIds((previous) => {
      const next = new Set(previous);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Both mutations apply their change at once and, on failure, undo only that
  // change rather than restoring a snapshot: a live notification, a read event
  // from another session or a re-read page that landed while the request was
  // in flight is newer than any snapshot and must survive the rollback. The
  // optimistic `readAt` Date doubles as the marker for "still exactly what
  // this call did" — anything else that touches an item's read state replaces
  // it — and the unread count is only put back while nothing has restated it
  // since (see unreadAuthorityRef).

  const markRead = useCallback(async (id) => {
    const target = itemsRef.current.find((item) => item.id === String(id));
    if (!target || target.read) return true;
    const decremented = unreadRef.current.count > 0;
    const authorityBefore = unreadAuthorityRef.current;
    const readAt = new Date();
    setMutationError(null);
    setPending(target.id, true);
    setItems((previous) => previous.map((item) => (item.id === target.id ? { ...item, read: true, readAt } : item)));
    setUnread((previous) => ({ ...previous, count: Math.max(0, previous.count - 1) }));
    try {
      const response = await fetchWrapperRef.current.post(NOTIFICATIONS_ENDPOINTS.markRead(target.id));
      if (response && typeof response === 'object' && response.unreadCount !== undefined) {
        restateUnread(normalizeUnreadCount({ count: response.unreadCount, capped: response.unreadCapped }));
      }
      setPending(target.id, false);
      return true;
    } catch (err) {
      console.error('Failed to mark notification read:', err);
      setItems((previous) => previous.map((item) => (item.id === target.id && item.readAt === readAt ? { ...item, read: false, readAt: null } : item)));
      if (decremented && unreadAuthorityRef.current === authorityBefore) {
        setUnread((previous) => ({ ...previous, count: previous.count + 1 }));
      }
      setPending(target.id, false);
      setMutationError({ message: tRef.current(FEED_MESSAGE_KEYS.markReadError), retry: () => markRead(target.id) });
      return false;
    }
  }, [setPending, restateUnread]);

  const markAllRead = useCallback(async () => {
    const before = unreadRef.current;
    if (before.count === 0 && !before.capped && itemsRef.current.every((item) => item.read)) return true;
    const authorityBefore = unreadAuthorityRef.current;
    const readAt = new Date();
    setMutationError(null);
    setItems((previous) => previous.map((item) => (item.read ? item : { ...item, read: true, readAt })));
    setUnread({ count: 0, capped: false });
    try {
      await fetchWrapperRef.current.post(NOTIFICATIONS_ENDPOINTS.markAllRead());
      return true;
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
      setItems((previous) => previous.map((item) => (item.readAt === readAt ? { ...item, read: false, readAt: null } : item)));
      if (unreadAuthorityRef.current === authorityBefore) {
        // Anything counted since arrived on top of the zero this call set.
        setUnread((previous) => ({ count: previous.count + before.count, capped: before.capped }));
      }
      setMutationError({ message: tRef.current(FEED_MESSAGE_KEYS.markAllReadError), retry: () => markAllRead() });
      return false;
    }
  }, []);

  const dismissMutationError = useCallback(() => setMutationError(null), []);

  return {
    items,
    unread,
    status,
    error,
    nextCursor,
    hasMore: Boolean(nextCursor),
    loadingMore,
    loadMoreError,
    mutationError,
    pendingIds,
    connectionStatus,
    announcement,
    reload,
    loadMore,
    markRead,
    markAllRead,
    dismissMutationError,
  };
}
